import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Alert, Stack, styled, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { filter } from 'es-toolkit/compat';
import React, { ReactElement, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { customerBucketAccessGrantsQueryKey, getCustomerBucketAccessGrants } from 'api/customerBucketAccessGrants';
import { OsTabs } from 'components/atoms/OsTabs';
import { StudyCustomerBucketAccessGrantsControl } from 'components/StudiesDashboard/StudyCustomerBucketAccessGrants/StudyCustomerBucketAccessGrantsControl';
import { Permission } from 'interfaces/permissionOption';
import { getAwsTemporaryCredentials } from 'services/S3Utils';
import { detectBrowserOperatingSystem } from 'utils/osDetection';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import { AWS_PROFILE_NAME } from './cliInstructionItNotes';
import CLIInstructions from './CLIInstructions';
import { OperatingSystem } from './cliInstructionTypes';
import { CliCommandBlock } from './CopyableCliBlock';
import CustomerGrantValidationStep from './CustomerGrantValidationStep';
import GrantBasedAwsUploadCommandGenerator from './GrantBasedAwsUploadCommandGenerator';
import LocalAwsUploadCommandGenerator from './LocalAwsUploadCommandGenerator';
import TemporaryAwsCredentialsStep from './TemporaryAwsCredentialsStep';
import { UploadBox } from './UploadSlidesFormUI';

const MainBox = styled(UploadBox)(() => ({
  flex: 1,
  minHeight: 0,
  marginBottom: 0,
  gap: 10,
  padding: '0 10px 0px',
  marginTop: '20px',
}));

const getSourcePathInstructions = (selectedOs: OperatingSystem) => {
  if (selectedOs === 'windows') {
    return (
      <Typography variant="body2" color="text.secondary">
        On Windows, select the file or folder in File Explorer, right-click it, and choose <strong>Copy as path</strong>{' '}
        from the menu.
      </Typography>
    );
  }

  if (selectedOs === 'linux') {
    return (
      <Typography variant="body2" color="text.secondary">
        On Linux, copy the file or folder path from your file manager&apos;s location bar, or drag the file or folder
        into the terminal to insert its path.
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      On macOS, select the file or folder in Finder and press <strong>Option + Command + C</strong> to copy its path.
    </Typography>
  );
};

interface Props {
  studyId: string;
}

const AwsProfileNameField = ({
  awsProfileName,
  credentialMode,
  setAwsProfileName,
}: {
  awsProfileName: string;
  credentialMode: 'temporary-nucleai' | 'customer-aws';
  setAwsProfileName: (awsProfileName: string) => void;
}) => (
  <TextField
    label="AWS profile name"
    value={awsProfileName}
    onChange={(event) => setAwsProfileName(event.target.value)}
    fullWidth
    placeholder={credentialMode === 'temporary-nucleai' ? AWS_PROFILE_NAME : 'my-upload-profile'}
    helperText={
      credentialMode === 'temporary-nucleai'
        ? 'Use one profile name for the configure and upload commands. This defaults to a dedicated Nucleai upload profile.'
        : 'Optional. If you already use an AWS CLI profile for uploads, enter it here so the configure and upload commands include --profile.'
    }
    sx={{ maxWidth: 420 }}
  />
);

const buildConfigureCommand = (awsProfileName: string) => {
  const trimmedAwsProfileName = awsProfileName.trim();

  return trimmedAwsProfileName ? `aws configure --profile ${trimmedAwsProfileName}` : 'aws configure';
};

const useStudySlideDirectory = (studyId: string) => {
  const { labId } = useCurrentLabId();
  const { data: labs, isLoading: isLoadingLabs } = useLabs();
  const customerBucketName = labs?.find((lab) => lab.id === labId)?.customerBucketName;
  const resolvedBucketName = customerBucketName || `nuc-cst-us-east-2-${labId}`;
  const slideDirectory = isLoadingLabs ? null : `s3://${resolvedBucketName}/ATOM/${studyId}/Incoming/`;

  return { isLoadingLabs, slideDirectory };
};

const GenerateCredentials = (props: Props): ReactElement => {
  const { studyId } = props;
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [awsProfileName, setAwsProfileName] = useState(AWS_PROFILE_NAME);

  const defaultOperatingSystem = detectBrowserOperatingSystem();
  const [selectedOsState, setSelectedOs] = useState<OperatingSystem | undefined>(undefined);
  const selectedOs = selectedOsState || defaultOperatingSystem;
  const [credentialsKeys, setCredentialsKeys] = useState({
    AccessKeyId: '',
    SecretAccessKey: '',
  });

  const [duringGenerate, setDuringGenerate] = useState(false);
  const [isGenerateFailed, setIsGenerateFailed] = useState(false);

  const { isLoadingLabs, slideDirectory } = useStudySlideDirectory(studyId);
  const region = 'us-east-2';

  const generateCredentials = async () => {
    if (isLoadingLabs) {
      return;
    }

    setIsGenerateFailed(false);
    setDuringGenerate(true);
    try {
      const requestedLabId = searchParams.get('labId');
      if (!requestedLabId) {
        setIsGenerateFailed(true);
        return;
      }

      const { Credentials } = await getAwsTemporaryCredentials(requestedLabId);
      setCredentialsKeys(Credentials);
    } catch {
      setIsGenerateFailed(true);
    } finally {
      setDuringGenerate(false);
    }
  };

  const exportExcelFile = () => {
    const data = [
      {
        AccessKeyId: credentialsKeys.AccessKeyId,
        SecretAccessKey: credentialsKeys.SecretAccessKey,
        Region: region,
      },
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Credentials');
    XLSX.writeFile(workbook, 'user-credentials.xlsx');
  };

  return (
    <>
      <MainBox data-testid="nucleai-storage-container">
        {slideDirectory ? (
          <CLIInstructions
            connectStepContent={
              <Stack spacing={1.5}>
                <AwsProfileNameField
                  awsProfileName={awsProfileName}
                  credentialMode="temporary-nucleai"
                  setAwsProfileName={setAwsProfileName}
                />
                <TemporaryAwsCredentialsStep
                  awsProfileName={awsProfileName}
                  credentials={credentialsKeys}
                  isError={isGenerateFailed}
                  isGenerating={duringGenerate}
                  isLoading={isLoadingLabs}
                  onExport={exportExcelFile}
                  onGenerate={generateCredentials}
                  region={region}
                />
              </Stack>
            }
            selectedOs={selectedOs}
            setSelectedOs={setSelectedOs}
            title="Upload slide from this computer"
            studyId={studyId}
            uploadStepContent={
              <Stack spacing={1.5}>
                <AwsProfileNameField
                  awsProfileName={awsProfileName}
                  credentialMode="temporary-nucleai"
                  setAwsProfileName={setAwsProfileName}
                />
                <Typography variant="body2" color="text.secondary">
                  Choose your computer type for source path instructions.
                </Typography>
                <OsTabs
                  selectedOs={selectedOs}
                  setSelectedOs={setSelectedOs}
                  ariaLabel="Source path instructions by operating system"
                />
                <LocalAwsUploadCommandGenerator
                  awsProfileName={awsProfileName.trim()}
                  destination={slideDirectory}
                  selectedOs={selectedOs}
                  sourcePathInstructions={getSourcePathInstructions(selectedOs)}
                />
              </Stack>
            }
            uploadStepTitle="Upload slides"
          />
        ) : (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Loading lab storage instructions...
            </Typography>
          </Box>
        )}
      </MainBox>
    </>
  );
};

export const CustomerBucketGrantUploadInstructions = ({ studyId }: { studyId: string }) => {
  const { hasPermission } = usePermissions();
  const { slideDirectory } = useStudySlideDirectory(studyId);
  const [awsProfileName, setAwsProfileName] = useState('');
  const [selectedOs, setSelectedOs] = useState<OperatingSystem>('mac');
  const canManageGrants =
    hasPermission(Permission.EditLabCustomerBucketAccessGrants) ||
    hasPermission(Permission.CreateOwnCustomerBucketAccessGrants);
  const grantsQuery = useQuery({
    queryKey: customerBucketAccessGrantsQueryKey(studyId),
    queryFn: ({ signal }) => getCustomerBucketAccessGrants(studyId, signal),
    enabled: Boolean(studyId) && canManageGrants,
  });
  const activeGrantCount = filter(grantsQuery.data || [], (grant) => grant.status === 'active').length;

  return (
    <MainBox data-testid="customer-bucket-grants-container">
      <CLIInstructions
        connectStepContent={
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Copy this command into the same terminal window and enter the AWS access key, secret key, default region,
              and output format your AWS administrator approved for uploads.
            </Typography>
            <AwsProfileNameField
              awsProfileName={awsProfileName}
              credentialMode="customer-aws"
              setAwsProfileName={setAwsProfileName}
            />
            <CliCommandBlock command={buildConfigureCommand(awsProfileName)} />
            <Typography variant="body2" color="text.secondary">
              What you should expect: AWS CLI will prompt for the access key, secret key, default region, and output
              format. Use json for the output format unless your AWS administrator told you otherwise.
            </Typography>
          </Stack>
        }
        configureStep={{
          title: 'Configure AWS upload access',
          icon: <VpnKeyOutlinedIcon color="primary" fontSize="small" />,
          isReady: activeGrantCount > 0,
          content: (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Use this step to add the AWS role or user that will run the upload command. The next validation step
                checks that Nucleai is ready to receive the upload. Nucleai configures the destination bucket; your AWS
                administrator must allow the role or user to read your source data and write to the generated
                destination path.
              </Typography>
              {activeGrantCount > 0 ? (
                <Alert severity="success">
                  AWS upload access is ready. If you plan to upload with one of the users below, you can skip this step
                  or add another user.
                </Alert>
              ) : null}
              <StudyCustomerBucketAccessGrantsControl
                awsProfileName={awsProfileName}
                hasConfiguredAccess={activeGrantCount > 0}
                setAwsProfileName={setAwsProfileName}
                studyId={studyId}
              />
            </Stack>
          ),
        }}
        credentialMode="customer-aws"
        itNotesDestination={slideDirectory || ''}
        selectedOs={selectedOs}
        setSelectedOs={setSelectedOs}
        title="Upload slide from your organization's Amazon S3 bucket"
        studyId={studyId}
        uploadStepContent={
          <Stack spacing={1.5}>
            <AwsProfileNameField
              awsProfileName={awsProfileName}
              credentialMode="customer-aws"
              setAwsProfileName={setAwsProfileName}
            />
            <GrantBasedAwsUploadCommandGenerator
              awsProfileName={awsProfileName.trim()}
              slideDirectory={slideDirectory || ''}
            />
          </Stack>
        }
        uploadStepTitle="Upload slides"
        validateStep={{
          title: 'Validate AWS upload permissions',
          icon: <SecurityOutlinedIcon color="primary" fontSize="small" />,
          content: <CustomerGrantValidationStep studyId={studyId} />,
        }}
      />
    </MainBox>
  );
};

export default GenerateCredentials;
